import { useEffect, useMemo, useRef, useState } from "react";
import { Check, LoaderCircle } from "lucide-react";
import { useSearchParams } from "react-router";
import type { BillingInterval, BillingView, PaidPlanId, PlanView } from "@shared/lib/billing-shared";
import { apiFetch } from "@shared/client/api";
import { useIntlLocale, useT } from "@shared/client/locale";
import { useRouter } from "@shared/client/navigation";
import { useView } from "@shared/client/view";
import { BillingSkeleton } from "@/components/page-skeletons";
import { ViewFallback } from "@/components/page-states";
import { StatusDot } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Progress } from "@shared/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@shared/components/ui/tabs";
import { cn } from "@shared/lib/utils";

/**
 * L'abonnement du compte : le plan en cours et son usage, le comparatif des
 * trois plans, le paiement par Stripe Checkout et la gestion par le portail
 * Stripe. Le retour de Checkout (`?billing=success&session_id=…`) confirme
 * l'abonnement sans attendre le webhook.
 */

/** Prices in euros, formatted for the interface language. */
function useEuros() {
  const intlLocale = useIntlLocale();
  return useMemo(() => {
    const format = new Intl.NumberFormat(intlLocale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
    return (cents: number) => format.format(cents / 100);
  }, [intlLocale]);
}

// Stripe status → tone of the dot; labels in `settings.billing.statuses`.
const STATUS_TONE: Record<string, string> = {
  active: "active",
  trialing: "active",
  past_due: "danger",
  unpaid: "danger",
  canceled: "neutral",
  incomplete: "neutral",
};

function cadenceKey(days: number) {
  if (days >= 30) return "monthly" as const;
  if (days >= 7) return "weekly" as const;
  return "twiceWeekly" as const;
}

function perMonth(plan: PlanView, interval: BillingInterval) {
  return interval === "yearly" ? plan.yearlyPriceCents / 12 : plan.monthlyPriceCents;
}

function Measure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid content-start gap-2 border-r p-4 last:border-r-0 max-sm:p-3">
      <span className="text-[12.5px] text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/** Le plan en vigueur : son nom, son prix ou son échéance, et ce qu'il ouvre. */
function CurrentPlan({ data, action }: { data: BillingView; action?: React.ReactNode }) {
  const t = useT().settings.billing;
  const euros = useEuros();
  const intlLocale = useIntlLocale();
  const { plan, subscription } = data;
  const status = subscription
    ? {
        tone: STATUS_TONE[subscription.status] ?? "neutral",
        label: t.statuses[subscription.status] ?? subscription.status,
      }
    : null;
  const price = data.isAdmin
    ? t.adminAccount
    : plan.monthlyPriceCents === 0
      ? t.free
      : subscription?.interval === "yearly"
        ? t.pricePerYear(euros(plan.yearlyPriceCents))
        : t.pricePerMonth(euros(plan.monthlyPriceCents));
  const periodEnd = subscription?.currentPeriodEnd
    ? new Intl.DateTimeFormat(intlLocale, { day: "numeric", month: "long", year: "numeric" }).format(
        new Date(subscription.currentPeriodEnd),
      )
    : null;
  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-start justify-between gap-4 p-5 max-sm:p-4">
        <div className="grid gap-1.5">
          <span className="text-[13px] text-muted-foreground">{t.yourPlan}</span>
          <p className="m-0 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="font-serif text-[30px] leading-none tracking-[-0.02em]">{plan.name}</span>
            <span className="text-[14px] text-muted-foreground tabular-nums">{price}</span>
          </p>
          {(status || periodEnd) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {status && <StatusDot tone={status.tone}>{status.label}</StatusDot>}
              {periodEnd && (
                <span className="text-[12.5px] text-muted-foreground">
                  {subscription?.cancelAtPeriodEnd ? t.endsOn(periodEnd) : t.renewsOn(periodEnd)}
                </span>
              )}
            </div>
          )}
        </div>
        {action}
      </div>
      <div className="grid grid-cols-3 border-t">
        <Measure label={t.trackedOffers}>
          <span className="text-[20px] font-semibold tabular-nums max-sm:text-[17px]">
            {data.offersUsed}
            <span className="text-[14px] font-normal text-muted-foreground"> / {plan.maxOffers}</span>
          </span>
          <Progress aria-label={t.trackedOffers} className="h-1" value={Math.min(100, (data.offersUsed / plan.maxOffers) * 100)} />
        </Measure>
        <Measure label={t.watch}>
          <span className="text-[20px] font-semibold max-sm:text-[17px]">{t.cadence[cadenceKey(plan.minFrequencyDays)]}</span>
        </Measure>
        <Measure label={t.manualRuns}>
          <span className="text-[20px] font-semibold max-sm:text-[17px]">{plan.manualRuns ? t.unlimited : t.oneFree}</span>
        </Measure>
      </div>
    </div>
  );
}

/** Comparatif : une colonne par plan, celle du plan en vigueur grisée. */
function PlanTable({
  data,
  interval,
  choose,
}: {
  data: BillingView;
  interval: BillingInterval;
  choose?: { busy: string | null; disabled: boolean; onChoose: (plan: PaidPlanId) => void };
}) {
  const t = useT().settings.billing;
  const euros = useEuros();
  const check = <Check aria-label={t.included} className="mx-auto size-4" />;
  const rows: { label: string; value: (plan: PlanView) => React.ReactNode }[] = [
    { label: t.trackedOffers, value: (plan) => plan.maxOffers },
    { label: t.watch, value: (plan) => t.cadence[cadenceKey(plan.minFrequencyDays)] },
    { label: t.manualRuns, value: (plan) => (plan.manualRuns ? check : t.oneFree) },
    { label: t.research, value: () => check },
  ];
  const cell = (plan: PlanView) => cn("border-l text-center", plan.id === data.plan.id && "bg-muted/60");
  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full table-fixed border-collapse text-[13.5px] max-sm:text-[12.5px]">
        <thead>
          <tr>
            <th className="w-[34%] max-sm:w-[27%]">
              <span className="sr-only">{t.planColumn}</span>
            </th>
            {data.plans.map((plan) => (
              <th className={cn(cell(plan), "p-4 font-normal max-sm:px-1 max-sm:py-3")} key={plan.id} scope="col">
                <div className="text-[14px] font-semibold">{plan.name}</div>
                <div className="mt-1 text-[22px] font-semibold tracking-tight tabular-nums max-sm:text-[18px]">
                  {euros(perMonth(plan, interval))}
                </div>
                <div className="text-[12px] text-muted-foreground">{plan.monthlyPriceCents ? t.perMonthUnit : " "}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr className="border-t" key={row.label}>
              <th className="px-4 py-3 text-left font-normal text-muted-foreground max-sm:px-2" scope="row">
                {row.label}
              </th>
              {data.plans.map((plan) => (
                <td className={cn(cell(plan), "px-2 py-3 tabular-nums max-sm:px-1")} key={plan.id}>
                  {row.value(plan)}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t">
            <td />
            {data.plans.map((plan) => (
              <td className={cn(cell(plan), "p-3 max-sm:px-1")} key={plan.id}>
                {plan.id === data.plan.id ? (
                  <StatusDot className="max-sm:whitespace-normal" tone="active">{t.currentPlan}</StatusDot>
                ) : choose && plan.id !== "free" ? (
                  <Button
                    className="w-full"
                    disabled={choose.disabled}
                    onClick={() => choose.onChoose(plan.id as PaidPlanId)}
                    size="sm"
                    type="button"
                    variant={plan.id === "pro" ? "default" : "outline"}
                  >
                    {choose.busy === plan.id && <LoaderCircle className="animate-spin" />} {t.choose}
                  </Button>
                ) : null}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function BillingSection() {
  const t = useT().settings.billing;
  const router = useRouter();
  const [params, setParams] = useSearchParams();
  const { data, error: loadError } = useView<BillingView>("/api/views/billing");
  const [interval, setInterval] = useState<BillingInterval>("monthly");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const confirmed = useRef(false);

  // Retour de Stripe Checkout.
  useEffect(() => {
    const outcome = params.get("billing");
    const sessionId = params.get("session_id");
    if (!outcome || confirmed.current) return;
    confirmed.current = true;
    const clear = () => setParams({}, { replace: true });
    if (outcome === "success" && sessionId) {
      apiFetch<{ ok: boolean }>("/api/billing/confirm", { method: "POST", body: JSON.stringify({ sessionId }) })
        .then(() => {
          setNotice(t.thanks);
          router.refresh();
        })
        .catch(() => setNotice(t.paymentReceived))
        .finally(clear);
    } else {
      clear();
    }
  }, [params, setParams, router, t]);

  async function go(path: string, body: object, key: string) {
    setError("");
    setBusy(key);
    try {
      const { url } = await apiFetch<{ url: string }>(path, { method: "POST", body: JSON.stringify(body) });
      window.location.assign(url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.unavailable);
      setBusy(null);
    }
  }

  if (!data) {
    return <ViewFallback error={loadError} placement="section" skeleton={<BillingSkeleton />} subject={t.loadSubject} />;
  }
  const subscribed = data.subscription !== null && data.plan.id !== "free";

  return (
    <div className="grid gap-5">
      {notice && (
        <p className="m-0 flex items-center gap-2 text-[12.5px] font-medium text-success">
          <Check className="size-4" aria-hidden="true" /> {notice}
        </p>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <CurrentPlan
        action={
          subscribed ? (
            <Button
              disabled={busy !== null || !data.enabled}
              onClick={() => void go("/api/billing/portal", {}, "portal")}
              type="button"
              variant="outline"
            >
              {busy === "portal" && <LoaderCircle className="animate-spin" />} {t.manage}
            </Button>
          ) : undefined
        }
        data={data}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[13px] font-medium">{t.compare}</span>
        {!subscribed && (
          <Tabs onValueChange={(value) => setInterval(value as BillingInterval)} value={interval}>
            <TabsList aria-label={t.interval} className="h-8">
              <TabsTrigger className="px-2.5 text-[13px]" value="monthly">{t.monthly}</TabsTrigger>
              <TabsTrigger className="px-2.5 text-[13px]" value="yearly">{t.yearly}</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>
      <PlanTable
        choose={
          subscribed
            ? undefined
            : {
                busy,
                disabled: busy !== null || !data.enabled,
                onChoose: (plan) => void go("/api/billing/checkout", { plan, interval }, plan),
              }
        }
        data={data}
        interval={interval}
      />
      {!data.enabled && !subscribed && (
        <p className="m-0 text-[12.5px] text-muted-foreground">{t.comingSoon}</p>
      )}
      {subscribed && (
        <p className="m-0 text-[12.5px] text-muted-foreground">{t.manageHint}</p>
      )}
    </div>
  );
}
